import { App } from '../App'
import { Clock, Color, PerspectiveCamera, Scene, Vector2, Vector3, WebGLRenderer } from 'three'
import styles from './Stage.module.css'
import { Moebius, MoebiusConfig } from './Moebius'
import { Edit } from './Edit'
import { Colors, Controller } from '../const'

export class StageController implements Controller {
  private readonly camera = new PerspectiveCamera(35, 1, 0.1, 1000)
  private readonly clock = new Clock(true)

  public readonly moebius: Moebius
  public readonly debug: Edit

  public readonly renderer: WebGLRenderer
  public readonly scene = new Scene()
  public readonly cameraPosition = new Vector3()
  public readonly pointer = new Vector2()

  private configs: MoebiusConfig[] = []
  private nodes: HTMLElement[] = []

  public width = 1
  public height = 1
  private tops: number[] = []
  private needsUpdate = true
  private activeIndex = -1

  constructor(
    public readonly node: HTMLElement,
    private readonly app: App,
  ) {
    this.onIntersect = this.onIntersect.bind(this)

    this.app.on('intersect', this.onIntersect)
    this.app.intersectionObserver.observe(this.node)

    this.renderer = new WebGLRenderer({ antialias: true, preserveDrawingBuffer: true })
    this.renderer.setSize(this.width, this.height)
    this.renderer.setPixelRatio(window.devicePixelRatio)
    this.renderer.setClearColor(new Color(Colors.grey), 1)
    this.node.appendChild(this.renderer.domElement)

    this.camera.position.set(0, 0, 5)
    this.scene.add(this.camera)

    this.updateConfigs()

    this.moebius = new Moebius(this)
    // this.typo = new Typo(this)
    this.debug = new Edit(this)
  }

  async load(): Promise<void> {
    await Promise.all([this.moebius.load()])
  }

  async show(): Promise<void> {
    this.moebius.show()
    // await delay(1000)
    // this.typo.show()
  }

  onIntersect(entries: IntersectionObserverEntry[]): void {
    entries.forEach((entry) => {
      if (entry.target === this.node) {
      }
    })
  }

  scroll() {
    this.needsUpdate = true
  }

  updateConfigs() {
    this.nodes = Array.from(document.body.querySelectorAll(`.${styles.Node}`) as NodeListOf<HTMLElement>)
    this.configs = this.nodes.map((node) => {
      const config = JSON.parse(decodeURI(node.dataset?.config || '')) || {}
      return {
        scale: config.scale || 1,
        fade: config.fade || 1,
        speed: config.speed || 1,
        tilt: config.tilt || 0,
        pivot: config.pivot || 0,
        spin: config.spin || 1,
        image: config.image || '',
        rotation: config.rotation || new Vector2(),
        position: config.position || new Vector2(),
      }
    })
    this.activeIndex = -1
  }

  setConfig(config: MoebiusConfig | null) {
    if (config) {
      this.moebius.setConfig(config)
    } else {
      this.activeIndex = -1
      this.needsUpdate = true
    }
  }

  public resize(): void {
    const { scrollY } = this.app
    const { width, height } = this.node.getBoundingClientRect()

    this.tops = this.nodes.map((node) => node.getBoundingClientRect().top + scrollY)
    this.width = width
    this.height = height

    this.renderer.setSize(this.width, this.height)

    this.camera.aspect = this.width / this.height
    this.camera.updateProjectionMatrix()
  }

  update(time: number) {
    const { pointerX, pointerY } = this.app
    const delta = Math.min(this.clock.getDelta(), 0.05)
    this.pointer.set((pointerX / this.width) * 2 - 1, -(pointerY / this.height) * 2 + 1)

    if (this.needsUpdate) {
      this.needsUpdate = false
      let index = 0
      for (let i = 0; i < this.tops.length; i++) {
        const top = this.tops[i] - this.app.scrollY
        if (top > this.height) {
          break
        } else {
          index = i
        }
      }

      if (this.activeIndex !== index && !!this.configs[index]) {
        this.activeIndex = index
        this.moebius.setConfig(this.configs[index])
      }
    }

    this.camera.getWorldPosition(this.cameraPosition)
    this.moebius.update(time, delta)
    this.debug?.update()
    this.renderer.render(this.scene, this.camera)
  }
}
